import React from 'react';
import GhostLineMenu from './GhostLineMenu';
import { ghostLineLook, isAdminBrowser, ensureGhostFlagCss } from '../../utils/ghostLook';

// 📣 LIVE ROOM — one ghost-lane message rendered INSIDE the main feed
// (LIVE_ROOM_INLINE_GHOST_SPEC_2026_09_19.md, treatment A). Compact on
// purpose: residents and people keep their full cards, the crowd is small and
// dim so it can never out-talk them. No avatar, no card chrome, no per-line
// timestamp (hover shows it), no reactions. D4 ruled: NO markers of any kind.
//
// The ghost lane carries more than ghosts, and hiding the lane must not drop
// any of it: host lines (gold, the go-live ones are a tap target), system
// notes, GIFs, and the rare human line (unseated viewer / API).
// 👻 09-29 (Nina): that human line looks like the crowd's, and every line's ⋯
// (Report inside: GhostLineMenu) shows on hover or a tap (utils/ghostLook); an
// /admin-key browser keeps the old tell (the ⋯ on people's lines only, always on).
//
// ⚠️ This frontend has NO global box-sizing: border-box. Every padded
// full-width box here sets it, or it overflows the column on phones.
//
// 📣 09-25 `full` (Nina, "Crowd as full lines", with the fast stream): crowd
// lines at the card text's size (0.95em / 1.5) and full brightness, in the
// panel's own high-contrast text + readable accent. Host lines and system
// notes keep their look either way. Off = the small, dim line above.
const CrowdLine = React.memo(({ msg, THEME = {}, onWatchLive = null, full = false }) => {
  const [flagOpen, setFlagOpen] = React.useState(false);
  React.useEffect(() => { ensureGhostFlagCss(); }, []);
  if (!msg) return null;
  const isMod = !!msg.is_moderator;
  const isSystemNote = !!msg.is_reaction_note || msg.sender_id === 'system';
  const look = ghostLineLook(msg, { admin: isAdminBrowser(), seatsToo: true });
  const hoverFlag = look.flag === 'hover';
  const liveInvite = !!(isMod && onWatchLive
    && (msg.livestream_identity || /(?:just went|is) LIVE/.test(msg.message || '')));
  const liveInviteName = msg.livestream_streamer
    || ((msg.message || '').match(/🔴\s*(.+?)\s+(?:just went LIVE|is LIVE)/)?.[1] ?? null);
  const muted = THEME.textMuted || '#9aa3ad';
  const when = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : undefined;

  if (isSystemNote) {
    return (
      <div data-crowd-line="note" title={when} style={{
        boxSizing: 'border-box', width: '100%', padding: '1px 10px 5px',
        color: muted, fontSize: '0.74em', fontStyle: 'italic', opacity: 0.8,
        overflowWrap: 'anywhere',
      }}>
        {msg.message}
      </div>
    );
  }

  const fullLine = full && !isMod;
  const nameColor = isMod ? '#DAA520' : (fullLine ? (THEME.accentOnPanel || THEME.accent || '#A48CFF') : (THEME.accent || '#7C4DFF'));
  return (
    <div
      data-crowd-line={isMod ? 'host' : 'crowd'}
      data-crowd-full={fullLine ? '' : undefined}
      data-ghost-row={hoverFlag ? '' : undefined}
      data-ghost-open={hoverFlag && flagOpen ? '' : undefined}
      title={when}
      onClick={liveInvite ? () => onWatchLive({ identity: msg.livestream_identity || null, name: liveInviteName })
        : (hoverFlag ? () => setFlagOpen(o => !o) : undefined)}
      role={liveInvite ? 'button' : undefined}
      style={{
        boxSizing: 'border-box', width: '100%',
        padding: isMod ? '4px 10px 6px' : (fullLine ? '2px 14px 5px' : '1px 10px 5px'),
        margin: isMod ? '2px 0 6px' : 0,
        borderLeft: isMod ? '2px solid rgba(218,165,32,0.55)' : 'none',
        cursor: liveInvite ? 'pointer' : undefined,
        fontSize: isMod ? '0.86em' : (fullLine ? '0.95em' : '0.82em'),
        lineHeight: fullLine ? 1.5 : 1.4,
        color: isMod || fullLine ? (THEME.textOnPanel || THEME.textDark || '#f2f2f2') : muted,
        overflowWrap: 'anywhere', wordBreak: 'break-word',
      }}
    >
      <span style={{ color: nameColor, fontWeight: 700, marginRight: 6, opacity: isMod || fullLine ? 1 : 0.9 }}>
        {isMod ? `${msg.moderator_emoji || '🎙️'} ` : ''}{msg.sender}
      </span>
      {msg.message && <span style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</span>}
      {look.flag && (
        <span data-ghost-flag={hoverFlag ? '' : undefined} onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6, verticalAlign: 'middle' }}>
          <GhostLineMenu targetId={msg.id} targetPlayerId={msg.sender_id || null} onOpenChange={setFlagOpen} />
        </span>
      )}
      {liveInvite && (
        <span style={{
          marginLeft: 8, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap',
          border: '1px solid rgba(239,68,68,0.5)', backgroundColor: 'rgba(239,68,68,0.15)',
          color: '#ef4444', fontSize: '0.78em', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          ▶ Tap to watch
        </span>
      )}
      {msg.gif_url && (
        <img
          src={msg.gif_url}
          alt={msg.gif_title || 'GIF'}
          loading="lazy"
          style={{ display: 'block', maxWidth: 120, maxHeight: 90, borderRadius: 6, marginTop: 4 }}
        />
      )}
    </div>
  );
});

CrowdLine.displayName = 'CrowdLine';

export default CrowdLine;
